import React, { useRef, ReactNode, useEffect } from 'react';
import useState from 'react-usestateref';
import classnames from 'classnames';
import {
    ByzzerSelectOption,
    ByzzerChangeEvent,
    ByzzerConditionInput,
    ByzzerSearchableConditionInput,
    ByzzerChangeEventHandler,
    ByzzerSelectOptionGroup,
} from '@byzzer/ui-components';
import { useApp } from '@/contexts/UserContext';
import { useTenantApi } from '@/hooks/useTenantApi';
import { DemographicCondition } from '@/types/ReportRun';

export type ByzzerOshDemographicOption = {
    display: string;
    value: string;
};

export type ByzzerOshDemographicConditionInputProps = {
    value?: DemographicCondition;
    onChange?: ByzzerChangeEventHandler<DemographicCondition>;
    demographics?: ByzzerOshDemographicOption[];
    operationOptions: (ByzzerSelectOption | string | ByzzerSelectOptionGroup)[];
    maxAllowedValues?: number | undefined;
    includeLabels?: boolean;
    disabledKeys?: string[];
    searchableFields?: string[];
    search?: any;
    className?: string;
    actions?: ReactNode;
    name?: string;
    renderLargeList?: boolean;
};
const baseClassName = 'byz-osh-demographic-condition';

export const ByzzerOshDemographicConditionInput = ({
    value,
    onChange,
    demographics,
    includeLabels,
    disabledKeys,
    search,
    className,
    actions,
    name,
    renderLargeList,
    operationOptions,
    maxAllowedValues,
    ...props
}: ByzzerOshDemographicConditionInputProps) => {
    const ConditionInput: typeof ByzzerConditionInput = ByzzerConditionInput;
    const { getOshDemographics, getOshDemographicValues } = useTenantApi();
    const labels = includeLabels
        ? {
              keyLabel: 'Demographic',
              operationLabel: 'Condition',
              valueLabel: 'Values',
          }
        : {};

    const [demographicOptions, setDemographicOptions] = useState<string[]>([]);
    const [internalValue, setInternalValue] = useState<DemographicCondition>();
    const [internalValueOptions, setInternalValueOptions] = useState<
        (ByzzerSelectOption | string | ByzzerSelectOptionGroup)[]
    >([]);
    const loadedWithValueOptions = useRef<Boolean>(false);

    useEffect(() => {
        (async () => {
            try {
                const resp = await getOshDemographics();
                setDemographicOptions(resp);
            } catch (error) {
                console.log('error', error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!loadedWithValueOptions.current && value?.demographic && !internalValueOptions.length) {           
            // triggers dynamic lookup of value options on load, when value is passed in
            // console.log("(2) DemographicConditionInput - useEffect (INITIAL) / value (condition) LOADED ===>> ", value)
            handleKeyChange?.({
                value: value?.demographic,
                name,
            }, true); // Pass true to indicate this is initial load
        } else if (loadedWithValueOptions.current && value) {
            // not sure if &&value is needed
            setInternalValue(value);
        }
    }, [value]);

    useEffect(() => {
        if (!loadedWithValueOptions.current && internalValueOptions.length) {
            // responsible for setting 'value' (third select) after value options have loaded, when default value is passed into conditioninput
            // console.log("(3) DemographicConditionInput - useEffect (INITIAL)  / internalValueOptions updated - 'internalValueOptions' ===>> ", internalValueOptions)
            loadedWithValueOptions.current = true;
            setInternalValue(value);
        }
    }, [internalValueOptions]);

    // Convert string values to matching options from internalValueOptions for rendering
    const renderedValue = React.useMemo(() => {
        if (!internalValue?.value?.length) return [];
        
        // Map string values to their corresponding option objects
        const matchedOptions = internalValue.value
            .map((strValue: string) => {
                const matchedOption = internalValueOptions.find((opt) => {
                    if (typeof opt === 'string') return opt === strValue;
                    return (opt as ByzzerSelectOption).value === strValue;
                });
                return matchedOption || strValue;
            })
            .filter(Boolean);
        
        return maxAllowedValues === 1 
            ? (matchedOptions.length ? [matchedOptions[0]] : [])
            : matchedOptions;
    }, [internalValue?.value, internalValueOptions, maxAllowedValues]);

    function handleChange(e: any) {
        const rawValue = e.value?.value;
        const normalizedValues = Array.isArray(rawValue)
            ? rawValue.map(
                  (demographicValue: ByzzerSelectOption | string) =>
                      (demographicValue as ByzzerSelectOption)?.value ?? (demographicValue as string)
              )
            : rawValue
            ? [
                  (rawValue as ByzzerSelectOption)?.value ??
                      (rawValue as string),
              ]
            : [];

        const newValue = {
            demographic: e.value?.key,
            condition: e.value?.operation,
            value: normalizedValues,
            data: e.value.data,
        };

        if (onChange) {
            onChange?.({
                value: newValue,
                name,
            });
        } else {
            setInternalValue(newValue);
        }
    }

    async function handleKeyChange(e: ByzzerChangeEvent<string | undefined | null>, isInitialLoad: boolean = false) {
        if (e?.value) {
            const values = await getOshDemographicValues(e?.value);
            setInternalValueOptions(values);
            
            // Don't auto-populate if this is an initial load (data already exists)
            if (isInitialLoad) {
                return;
            }
            
            // Auto-select the operation when demographic is selected if:
            // 1. There's only one operation option available, OR
            // 2. No condition is currently set and there are operation options
            const shouldAutoSelect = operationOptions.length === 1 || 
                (!internalValue?.condition && operationOptions.length > 0);
            
            if (shouldAutoSelect) {
                const defaultCondition = typeof operationOptions[0] === 'string' 
                    ? operationOptions[0] 
                    : (operationOptions[0] as ByzzerSelectOption).value;
                
                const newValue: DemographicCondition = {
                    demographic: e.value,
                    condition: defaultCondition as any,
                    value: internalValue?.value || [],
                    data: internalValue?.data || {},
                };
                
                if (onChange) {
                    onChange?.({
                        value: newValue,
                        name,
                    });
                } else {
                    setInternalValue(newValue);
                }
            }
        }
    }

    return (
        <ConditionInput
            // {...props}
            {...labels}
            value={{
                key: internalValue?.demographic,
                operation: internalValue?.condition || (operationOptions.length > 0 
                    ? (typeof operationOptions[0] === 'string' 
                        ? operationOptions[0] 
                        : (operationOptions[0] as ByzzerSelectOption).value)
                    : 'is'),
                value: renderedValue as any,
                data: internalValue?.data,
            }}
            className={classnames(baseClassName, className)}
            keyOptions={demographicOptions}
            operationOptions={operationOptions}
            allowMultipleValues={maxAllowedValues !== 1}
            valueOptions={internalValueOptions}
            keyReactSelectOptions={{}}
            operationReactSelectOptions={{}}
            valueReactSelectOptions={{}}
            onKeyChange={handleKeyChange}
            disabledKeys={disabledKeys}
            actions={actions}
            name={name}
            onChange={handleChange}
            renderLargeList={renderLargeList}
            maxAllowedValues={maxAllowedValues}
        />
    );
};

export default ByzzerOshDemographicConditionInput;

ByzzerOshDemographicConditionInput.displayName = 'ByzzerOshDemographicConditionInput';
